import { useState, useEffect, useCallback } from 'react';
import { PriceMap } from '../types';
import { fetchStockSpot } from './useStockPrices';

/**
 * Gold & silver spot prices, now sourced from the SAME Yahoo proxy as stocks
 * (metals.live is dead). GC=F / SI=F are the front-month futures — spot-equivalent
 * USD per troy ounce, so they preserve the ounce unit the app already prices metal
 * holdings in (GLD/SLV are ~1/10-oz ETF shares and would misprice holdings).
 *
 * fetchStockSpot fetches /api/stock-quote and converts the native price → USD
 * (both metals quote in USD, so it's an identity conversion) and derives the 24h
 * change from Yahoo's previous close — no rolling-reference workaround needed.
 *
 * Output contract is unchanged: a PriceMap keyed 'xau-gold' / 'xag-silver' in USD.
 * A metal whose quote is unavailable is ABSENT from the map (not 0), so downstream
 * can tell "no price" apart from a real zero.
 */

const CACHE_KEY = 'mycryptoplanner_metal_prices';
const CACHE_TTL = 60_000;

const METAL_TICKERS: { id: string; ticker: string }[] = [
  { id: 'xau-gold',   ticker: 'GC=F' },
  { id: 'xag-silver', ticker: 'SI=F' },
];

interface MetalCacheEntry {
  usd: number;
  usd_24h_change: number;
  fetchedAt: number;
}
type MetalCache = Record<string, MetalCacheEntry>; // keyed by metal id

function loadCache(): MetalCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as MetalCache) : {};
  } catch { return {}; }
}

function saveCache(cache: MetalCache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* quota */ }
}

function cacheToMap(cache: MetalCache): PriceMap {
  const map: PriceMap = {};
  for (const { id } of METAL_TICKERS) {
    const e = cache[id];
    if (e) map[id] = { usd: e.usd, usd_24h_change: e.usd_24h_change };
  }
  return map;
}

export function useMetalPrices() {
  const [prices,  setPrices]  = useState<PriceMap>(() => cacheToMap(loadCache()));
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchPrices = useCallback(async (force = false) => {
    const cache = loadCache();
    const now   = Date.now();
    const allFresh = METAL_TICKERS.every(
      ({ id }) => cache[id] && now - cache[id].fetchedAt < CACHE_TTL
    );

    if (!force && allFresh) {
      setPrices(cacheToMap(cache));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      // Per-metal isolation: one dead ticker doesn't blank the other.
      const results = await Promise.allSettled(
        METAL_TICKERS.map(async ({ id, ticker }) => ({ id, ...(await fetchStockSpot(ticker)) }))
      );

      const newCache: MetalCache = { ...loadCache() };
      let anyOk = false;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          newCache[r.value.id] = {
            usd: r.value.usd,
            usd_24h_change: r.value.usd_24h_change,
            fetchedAt: now,
          };
          anyOk = true;
        }
      }
      saveCache(newCache);
      setPrices(cacheToMap(newCache));
      // Surface unavailability only when nothing resolved (and nothing is cached to
      // fall back on) — a partial success still shows what it can.
      if (!anyOk && Object.keys(newCache).length === 0) {
        setError('Live metal prices are unavailable.');
      }
    } catch {
      setPrices(cacheToMap(loadCache()));
      setError('Live metal prices are unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices(false);
    const id = setInterval(() => fetchPrices(true), CACHE_TTL);
    return () => clearInterval(id);
  }, [fetchPrices]);

  return { prices, loading, error };
}
